import { useState, type ReactNode } from "react";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import type { FaqItem } from "@/components/feature/FaqAccordion";

interface EnquirySectionProps {
  eyebrow: string;
  heading: string;
  intro?: string;
  faqs?: FaqItem[];
  panelHeading: string;
  panelIntro: ReactNode;
  buttonLabel?: string;
  bullets?: string[];
  onEnquire: () => void;
  id?: string;
}

const DEFAULT_BULLETS = [
  "Reply within 24 hours",
  "No payment to enquire",
  "Free to reschedule with notice",
];

export default function EnquirySection({
  eyebrow,
  heading,
  intro,
  faqs,
  panelHeading,
  panelIntro,
  buttonLabel = "Open Enquiry Form",
  bullets = DEFAULT_BULLETS,
  onEnquire,
  id = "enquire",
}: EnquirySectionProps) {
  const [open, setOpen] = useState<number | null>(0);
  const hasFaqs = Boolean(faqs && faqs.length > 0);

  return (
    <section id={id} className="sun-glaze bg-secondary-50 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-12 md:mb-14">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              {eyebrow}
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
            {heading}
          </h2>
          {intro ? (
            <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">{intro}</p>
          ) : null}
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-start">
          {hasFaqs && (
            <Reveal className="lg:col-span-5">
              <div className="bg-background-100 rounded-lg border border-background-200 p-7 md:p-9">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-question-line text-2xl"></i>
                </span>
                <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
                  Frequently Asked
                </h3>

                <div className="mt-6 divide-y divide-background-200">
                  {faqs!.map((faq, index) => {
                    const isOpen = open === index;
                    return (
                      <div key={faq.q}>
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : index)}
                          className="w-full flex items-center justify-between gap-4 py-4 text-left cursor-pointer"
                          aria-expanded={isOpen}
                        >
                          <span className="text-sm md:text-base font-semibold text-foreground-900">
                            {faq.q}
                          </span>
                          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-700 shrink-0">
                            <i className={`${isOpen ? "ri-subtract-line" : "ri-add-line"}`}></i>
                          </span>
                        </button>
                        {isOpen && (
                          <p className="pb-5 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          )}

          <Reveal className={hasFaqs ? "lg:col-span-7" : "lg:col-span-12"} delay={100}>
            <div
              className={`bg-primary-900 rounded-lg p-7 md:p-9 lg:sticky lg:top-24 ${
                hasFaqs ? "" : "max-w-2xl"
              }`}
            >
              <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                <i className="ri-mail-send-line text-2xl"></i>
              </span>
              <h3 className="font-heading text-2xl md:text-3xl text-background-50 font-semibold mt-5">
                {panelHeading}
              </h3>
              <p className="text-sm text-background-200 leading-relaxed mt-3">{panelIntro}</p>

              <button
                type="button"
                onClick={onEnquire}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-send-line text-lg"></i>
                {buttonLabel}
              </button>

              <ul className="mt-7 space-y-3 border-t border-primary-700 pt-6">
                {bullets.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-800 text-accent-300 shrink-0">
                      <i className="ri-check-line text-sm"></i>
                    </span>
                    <span className="text-sm text-background-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}